import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  useParams,
} from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Toaster } from "react-hot-toast";
import { useLanguageSync } from "./hooks/useLanguageSync";
import LanguageRedirect from "./components/routing/LanguageRedirect";

// Componente para redireccionar a ruta con idioma detectado
const RedirectToDetectedLanguage: React.FC<{ path: string }> = ({ path }) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const targetLang = i18n.language || "en";
  return <Navigate to={`/${targetLang}${path}${location.search}`} replace />;
};

// Componente para redireccionar blog con ID dinámico
const RedirectBlogPost: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { i18n } = useTranslation();
  return <Navigate to={`/${i18n.language}/blog/${id}`} replace />;
};

// ============================================
// 🚀 LAZY LOADING - Mejora de rendimiento
// Dividimos los componentes en chunks para reducir el bundle inicial
// ============================================

// Layouts - se cargan inmediatamente para evitar flash
import PublicLayout from "./layouts/PublicLayout";

const SUPPORTED_PUBLIC_LANGUAGES = ["en", "es", "fr"];

const SupportedPublicLayout: React.FC = () => {
  const { lang } = useParams<{ lang: string }>();

  if (!lang || !SUPPORTED_PUBLIC_LANGUAGES.includes(lang)) {
    return <NotFoundPage />;
  }

  return <PublicLayout />;
};

// Layouts privados - static imports
const ClientDashboardLayout = lazy(
  () => import("./layouts/ClientDashboardLayout"),
);
const TeacherDashboardLayout = lazy(
  () => import("./layouts/TeacherDashboardLayout"),
);
const AdminDashboardLayout = lazy(
  () => import("./layouts/AdminDashboardLayout"),
);

// Página principal - crítica, se carga primero
import HomePage from "./pages/public/HomePage";

// Páginas públicas secundarias - lazy load
const ServicesPage = lazy(() => import("./pages/public/ServicesPage"));
const PricingPage = lazy(() => import("./pages/public/PricingPage"));
const AboutPage = lazy(() => import("./pages/public/AboutPage"));
const ContactPage = lazy(() => import("./pages/public/ContactPage"));
const LoginPage = lazy(() => import("./pages/public/LoginPage"));
const SignUpPage = lazy(() => import("./pages/public/SignUpPage"));
const BlogPage = lazy(() => import("./pages/public/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/public/BlogPostPage"));
const VerifyEmailPage = lazy(() => import("./pages/public/VerifyEmailPage"));
const ResetPasswordPage = lazy(
  () => import("./pages/public/ResetPasswordPage"),
);
const RequestResetPasswordPage = lazy(
  () => import("./pages/public/RequestResetPasswordPage"),
);
const MagicLoginPage = lazy(() => import("./pages/public/MagicLoginPage"));
const Security = lazy(() => import("./pages/Security"));
const TermsAndConditions = lazy(
  () => import("./pages/public/TermsAndConditions"),
);
const GroupAdminSetup = lazy(() => import("./pages/public/GroupAdminSetup"));
const NotFoundPage = lazy(() => import("./pages/public/NotFoundPage"));
const MembershipSelectionPage = lazy(
  () => import("./pages/MembershipSelectionPage"),
);

// Componentes UI críticos - se cargan inmediatamente
import MovieIntroLoader from "./components/ui/MovieIntroLoader";
import NotificationContainer from "./components/ui/NotificationContainer";
import { curve, translate, anim } from "./lib/anim";
import "./styles/curve.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import PasskeyPromptBanner from "./components/auth/PasskeyPromptBanner";
import PostRegistrationContainer from "./components/auth/PostRegistrationContainer";
import { AppointmentProvider } from "./contexts/AppointmentContext";
import ScrollToTop from "./components/ui/ScrollToTop";
import { recaptchaService } from "./services/recaptchaService";

// FloatingAIChat - lazy load porque solo se usa en dashboards
const FloatingAIChat = lazy(() => import("./components/FloatingAIChat"));

// Páginas privadas - static imports
const ClientDashboard = lazy(() => import("./pages/private/ClientDashboard"));
const VideoConsulting = lazy(() => import("./pages/private/VideoConsulting"));
const AiChat = lazy(() => import("./pages/private/AiChat"));
const Templates = lazy(() => import("./pages/private/Templates"));
const Forum = lazy(() => import("./pages/private/Forum"));
const Resources = lazy(() => import("./pages/private/Resources"));
const TeacherDashboard = lazy(() => import("./pages/private/TeacherDashboard"));
const ProfilePage = lazy(() => import("./pages/private/ProfilePage"));
const AdminDashboard = lazy(() => import("./pages/private/AdminDashboard"));
const SOAPvett = lazy(() => import("./pages/private/SOAPvett"));
const ClinicFlow = lazy(() => import("./pages/private/ClinicFlow"));
const SOAPHistory = lazy(() => import("./pages/private/SOAPHistory"));
const SOAPConsultationDetail = lazy(
  () => import("./pages/private/SOAPConsultationDetail"),
);
const PhoneCallDetail = lazy(() => import("./pages/private/PhoneCallDetail"));
const SOAPEditorPage = lazy(() => import("./pages/private/SOAPEditor"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const ChatStatistics = lazy(() => import("./pages/admin/ChatStatistics"));
const DocumentManagement = lazy(
  () => import("./pages/admin/DocumentManagement"),
);
const ClaimsManagement = lazy(() => import("./pages/admin/ClaimsManagement"));
const GroupAdminDashboard = lazy(
  () => import("./pages/private/GroupAdminDashboard"),
);
const CaseLogPage = lazy(() => import("./pages/private/CaseLogPage"));
const TeleconsultRoom = lazy(() => import("./pages/private/TeleconsultRoom"));
const TeleconsultSummaryReview = lazy(
  () => import("./pages/private/TeleconsultSummaryReview"),
);

// Loading fallback minimalista para lazy components
const LazyLoadFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-transparent">
    <div className="animate-pulse flex space-x-2">
      <div className="w-2 h-2 bg-[#00e9be] rounded-full"></div>
      <div className="w-2 h-2 bg-[#00e9be] rounded-full animation-delay-200"></div>
      <div className="w-2 h-2 bg-[#00e9be] rounded-full animation-delay-400"></div>
    </div>
  </div>
);

interface Dimensions {
  width: number | null;
  height: number | null;
}

const SVG: React.FC<Dimensions> = ({ height, width }) => {
  if (width === null || height === null) return null;

  const initialPath = `
        M0 300
        Q${width / 2} 0 ${width} 300
        L${width} ${height + 300}
        Q${width / 2} ${height + 600} 0 ${height + 300}
        L0 0
    `;

  const targetPath = `
        M0 300
        Q${width / 2} 0 ${width} 300
        L${width} ${height}
        Q${width / 2} ${height} 0 ${height}
        L0 0
    `;

  return (
    <motion.svg className="curve-svg" {...anim(translate)}>
      <defs>
        <linearGradient id="curveGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#E8F5E8" />
        </linearGradient>
      </defs>
      <motion.path
        {...anim(curve(initialPath, targetPath))}
        fill="url(#curveGradient)"
      />
    </motion.svg>
  );
};

const AppContent: React.FC = () => {
  // 🚀 Detectar móvil INMEDIATAMENTE para skip loader
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  // En móvil, NO mostrar loader nunca
  const [showLoader, setShowLoader] = useState(!isMobile);
  const [isInitialLoad, setIsInitialLoad] = useState(!isMobile);
  const [isNavigating, setIsNavigating] = useState(false);
  const [dimensions, setDimensions] = useState<Dimensions>({
    width: null,
    height: null,
  });
  const location = useLocation();
  const previousLocation = useRef<string | null>(null);
  const [showSignUp, setShowSignUp] = useState(false);
  const { loading: authLoading, user } = useAuth();
  const { i18n } = useTranslation();

  // Sincronizar idioma con URL
  useLanguageSync();

  const loaderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getPersonalizedWelcomeMessage = (): string => {
    if (user) {
      const userName =
        user.firstName || user.displayName || user.email?.split("@")[0] || "";

      switch (i18n.language) {
        case "es":
          return userName
            ? `Bienvenido, ${userName}`
            : "Bienvenido a VettConsult";
        case "fr":
          return userName
            ? `Bienvenue, ${userName}`
            : "Bienvenue à VettConsult";
        case "en":
        default:
          return userName ? `Welcome, ${userName}` : "Welcome to VettConsult";
      }
    }

    return "Veterinary innovation around the world";
  };

  useEffect(() => {
    const initializeRecaptcha = async () => {
      try {
        await recaptchaService.initialize();
      } catch (error) {}
    };

    initializeRecaptcha();
  }, []);

  const handleLoaderComplete = () => {
    setShowLoader(false);
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
    setIsNavigating(false);
  };

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousLocation.current;

    if (previousPath && previousPath !== currentPath) {
      const scrollY = window.scrollY;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";

      requestAnimationFrame(() => {
        window.scrollTo(0, 0);

        requestAnimationFrame(() => {
          document.body.style.overflow = "";
          document.body.style.position = "";
          document.body.style.top = "";
          document.body.style.width = "";
        });
      });
    }

    previousLocation.current = currentPath;
  }, [location.pathname]);

  useEffect(() => {
    if (showLoader || isNavigating || authLoading) {
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
      }

      loaderTimeoutRef.current = setTimeout(() => {
        setShowLoader(false);
        setIsNavigating(false);
      }, 8000);
    } else {
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
        loaderTimeoutRef.current = null;
      }
    }

    return () => {
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
      }
    };
  }, [showLoader, isNavigating, authLoading]);

  useEffect(() => {
    const resize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    resize();
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);

  useEffect(() => {
    const isNavigatingToDashboard =
      previousLocation.current?.includes("/login") &&
      (location.pathname.includes("/dashboard") ||
        location.pathname.includes("/specialist") ||
        location.pathname.includes("/admin"));

    if (isNavigatingToDashboard) {
      setIsNavigating(true);

      setTimeout(() => {
        setIsNavigating(false);
      }, 3000);
    }

    if (
      !isInitialLoad &&
      location.pathname === "/" &&
      previousLocation.current !== "/"
    ) {
      setShowLoader(true);

      setTimeout(() => {
        setShowLoader(false);
      }, 3000);
    }
  }, [location.pathname, isInitialLoad]);

  // 🚀 En móvil, mostrar contenido INMEDIATAMENTE (solo esperar auth si es necesario)
  const showAppContent = isMobile
    ? !authLoading
    : !showLoader && !isNavigating && !authLoading;

  const shouldShowLoader = isMobile
    ? false
    : showLoader || isNavigating || authLoading;

  const getLoaderMessage = () => {
    if (showLoader && isInitialLoad) {
      return "Veterinary innovation around the world";
    }

    if (isNavigating || authLoading) {
      if (user) {
        return getPersonalizedWelcomeMessage();
      }
      return "Veterinary innovation around the world";
    }

    return "Veterinary innovation around the world";
  };

  const handleSwitchToLogin = () => {
    setShowSignUp(false);
  };

  const handleSwitchToSignUp = () => {
    setShowSignUp(true);
  };

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        backgroundColor: shouldShowLoader ? "#000000" : "transparent",
      }}
    >
      {/* MovieIntroLoader: Solo en desktop */}
      {!isMobile && (
        <MovieIntroLoader
          isLoading={shouldShowLoader}
          quote={getLoaderMessage()}
          minDuration={
            isInitialLoad
              ? 3000
              : (authLoading || isNavigating) && user
                ? 4000
                : 2000
          }
          onLoaded={handleLoaderComplete}
        />
      )}

      <NotificationContainer />

      {/* Passkey prompt – shown once after first login if device supports it */}
      <PasskeyPromptBanner />

      <div
        data-app-content
        style={{
          position: "relative",
          zIndex: 1,
          opacity: showAppContent ? 1 : 0,
          transition: isMobile ? "none" : "opacity 0.8s ease-in-out", // Sin transición en móvil
          pointerEvents: showAppContent ? "auto" : "none",
          minHeight: "100vh",
        }}
      >
        <ScrollToTop />
        <AnimatePresence mode="wait">
          <div key={location.pathname} className="page-curve-wrapper">
            {/* SVG curve animation: omitir en transiciones hacia/desde consulta */}
            {!isMobile && dimensions.width != null &&
              !location.pathname.includes("soapvett/consultation/") &&
              !previousLocation.current?.includes("soapvett/consultation/") && (
              <SVG {...dimensions} />
            )}

            <Suspense fallback={<LazyLoadFallback />}>
              <Routes location={location}>
                {/* Public routes with language prefix */}
                <Route path=":lang" element={<SupportedPublicLayout />}>
                  <Route index element={<HomePage />} />
                  <Route
                    path="services"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        <ServicesPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="pricing"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        <PricingPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="about"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        <AboutPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="contact"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        <ContactPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="privacy-policy"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        <Security />
                      </Suspense>
                    }
                  />
                  <Route
                    path="terms"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        <TermsAndConditions />
                      </Suspense>
                    }
                  />
                  <Route
                    path="blog"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        <BlogPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="blog/:id"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        <BlogPostPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="login"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        {showSignUp ? (
                          <SignUpPage onSwitchToLogin={handleSwitchToLogin} />
                        ) : (
                          <LoginPage onSwitchToSignUp={handleSwitchToSignUp} />
                        )}
                      </Suspense>
                    }
                  />
                  <Route
                    path="signup"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        <SignUpPage onSwitchToLogin={handleSwitchToLogin} />
                      </Suspense>
                    }
                  />
                  <Route
                    path="verify-email"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        <VerifyEmailPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="reset-password"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        <ResetPasswordPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="forgot-password"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        <RequestResetPasswordPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="magic-login"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        <MagicLoginPage />
                      </Suspense>
                    }
                  />
                  {/* Unknown sub-paths under a valid language -> real 404
                      (noindex) instead of bouncing to the homepage. */}
                  <Route
                    path="*"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        <NotFoundPage />
                      </Suspense>
                    }
                  />
                </Route>

                {/* Redirección raíz según idioma detectado del navegador */}
                <Route path="/" element={<LanguageRedirect />} />

                {/* Redirect legacy non-prefixed public paths to detected language */}
                <Route
                  path="/services"
                  element={<RedirectToDetectedLanguage path="/services" />}
                />
                <Route
                  path="/pricing"
                  element={<RedirectToDetectedLanguage path="/pricing" />}
                />
                <Route
                  path="/about"
                  element={<RedirectToDetectedLanguage path="/about" />}
                />
                <Route
                  path="/contact"
                  element={<RedirectToDetectedLanguage path="/contact" />}
                />
                <Route
                  path="/privacy-policy"
                  element={
                    <RedirectToDetectedLanguage path="/privacy-policy" />
                  }
                />
                <Route
                  path="/terms"
                  element={<RedirectToDetectedLanguage path="/terms" />}
                />
                <Route
                  path="/blog"
                  element={<RedirectToDetectedLanguage path="/blog" />}
                />
                <Route path="/blog/:id" element={<RedirectBlogPost />} />
                <Route
                  path="/login"
                  element={<RedirectToDetectedLanguage path="/login" />}
                />
                <Route
                  path="/magic-login"
                  element={<RedirectToDetectedLanguage path="/magic-login" />}
                />
                <Route
                  path="/signup"
                  element={<RedirectToDetectedLanguage path="/signup" />}
                />
                <Route
                  path="/verify-email"
                  element={<RedirectToDetectedLanguage path="/verify-email" />}
                />
                <Route
                  path="/reset-password"
                  element={
                    <RedirectToDetectedLanguage path="/reset-password" />
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <RedirectToDetectedLanguage path="/forgot-password" />
                  }
                />

                {/* Group Admin Setup - Public route (no auth needed) */}
                <Route
                  path="/group-setup/:groupId"
                  element={
                    <Suspense fallback={<LazyLoadFallback />}>
                      <GroupAdminSetup />
                    </Suspense>
                  }
                />

                {/* Membership selection — gate page outside of PostRegistrationContainer.
                    Auth-required but no plan-required, since this IS where the user picks
                    their plan. PostRegistrationContainer redirects here when no active plan. */}
                <Route
                  element={
                    <ProtectedRoute
                      requiredRoles={[
                        "user",
                        "cliente",
                        "teacher",
                        "especialista",
                        "admin",
                        "group_admin",
                      ]}
                      requireVerification={true}
                    />
                  }
                >
                  <Route
                    path="/membership-selection"
                    element={
                      <Suspense fallback={<LazyLoadFallback />}>
                        <MembershipSelectionPage />
                      </Suspense>
                    }
                  />
                </Route>

                {/* Rutas del Dashboard - accesibles para clientes, especialistas y admin */}
                <Route
                  element={
                    <ProtectedRoute
                      requiredRoles={[
                        "user",
                        "cliente",
                        "teacher",
                        "especialista",
                        "admin",
                        "group_admin",
                      ]}
                      requireVerification={true}
                    />
                  }
                >
                  <Route
                    path="/dashboard"
                    element={
                      <PostRegistrationContainer>
                        <ClientDashboardLayout />
                      </PostRegistrationContainer>
                    }
                  >
                    <Route index element={<ClientDashboard />} />
                    <Route path="vettcoach" element={<VideoConsulting />} />
                    <Route path="soapvett" element={<SOAPvett />} />
                    <Route path="soapvett/history" element={<SOAPHistory />} />
                    <Route path="soapvett/case-log" element={<CaseLogPage />} />
                    <Route
                      path="soapvett/consultation/:id"
                      element={<SOAPConsultationDetail />}
                    />
                    <Route
                      path="soapvett/phonecall/:id"
                      element={<PhoneCallDetail />}
                    />
                    <Route
                      path="soapvett/edit/:id"
                      element={<SOAPEditorPage />}
                    />
                    <Route path="clinicalflow" element={<ClinicFlow />} />
                    <Route path="clinicalflow/:id" element={<ClinicFlow />} />
                    <Route
                      path="clinicalflow/history"
                      element={<ClinicFlow />}
                    />
                    <Route path="nala" element={<AiChat />} />
                    <Route path="vettbox" element={<Templates />} />
                    <Route path="vettalk" element={<Forum />} />
                    <Route path="resources" element={<Resources />} />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="chat" element={<ChatPage />} />
                  </Route>
                </Route>

                {/* Rutas específicas del Teacher Dashboard - solo para especialistas y admin */}
                <Route
                  element={
                    <ProtectedRoute
                      requiredRoles={["teacher", "especialista", "admin"]}
                      requireVerification={true}
                    />
                  }
                >
                  <Route
                    path="/specialist"
                    element={
                      <PostRegistrationContainer>
                        <TeacherDashboardLayout />
                      </PostRegistrationContainer>
                    }
                  >
                    <Route index element={<TeacherDashboard />} />
                    <Route path="soapvett" element={<SOAPvett />} />
                    <Route path="soapvett/history" element={<SOAPHistory />} />
                    <Route path="soapvett/case-log" element={<CaseLogPage />} />
                    <Route
                      path="soapvett/consultation/:id"
                      element={<SOAPConsultationDetail />}
                    />
                    <Route
                      path="soapvett/phonecall/:id"
                      element={<PhoneCallDetail />}
                    />
                    <Route
                      path="soapvett/edit/:id"
                      element={<SOAPEditorPage />}
                    />
                    <Route path="clinicalflow" element={<ClinicFlow />} />
                    <Route path="clinicalflow/:id" element={<ClinicFlow />} />
                    <Route
                      path="clinicalflow/history"
                      element={<ClinicFlow />}
                    />
                    <Route path="nala" element={<AiChat />} />
                    <Route path="vettbox" element={<Templates />} />
                    <Route path="vettalk" element={<Forum />} />
                    <Route path="resources" element={<Resources />} />
                    <Route path="chat" element={<ChatPage />} />
                    <Route path="profile" element={<ProfilePage />} />
                  </Route>
                </Route>

                {/* Group Admin Dashboard - only for group_admin role */}
                <Route
                  element={
                    <ProtectedRoute
                      requiredRoles={["group_admin"]}
                      requireVerification={true}
                    />
                  }
                >
                  <Route
                    path="/group-admin"
                    element={
                      <PostRegistrationContainer>
                        <ClientDashboardLayout />
                      </PostRegistrationContainer>
                    }
                  >
                    <Route index element={<GroupAdminDashboard />} />
                  </Route>
                </Route>

                {/* Teleconsultation room — accessible to doctors and clients */}
                <Route
                  element={
                    <ProtectedRoute
                      requiredRoles={[
                        "user",
                        "cliente",
                        "teacher",
                        "especialista",
                        "admin",
                        "group_admin",
                      ]}
                      requireVerification={true}
                    />
                  }
                >
                  <Route
                    path="/teleconsult/room"
                    element={<TeleconsultRoom />}
                  />
                  <Route
                    path="/teleconsult/summary"
                    element={<TeleconsultSummaryReview />}
                  />
                </Route>

                <Route
                  element={
                    <ProtectedRoute
                      requiredRoles={["admin"]}
                      requireVerification={true}
                    />
                  }
                >
                  <Route
                    path="/admin"
                    element={
                      <PostRegistrationContainer>
                        <AdminDashboardLayout />
                      </PostRegistrationContainer>
                    }
                  >
                    <Route index element={<AdminDashboard />} />
                    <Route
                      path="users"
                      element={<AdminDashboard activeTab="users" />}
                    />
                    <Route
                      path="calendars"
                      element={<AdminDashboard activeTab="calendars" />}
                    />
                    <Route
                      path="templates"
                      element={<AdminDashboard activeTab="templates" />}
                    />
                    <Route
                      path="blog"
                      element={<AdminDashboard activeTab="blog" />}
                    />
                    <Route path="soapvett" element={<SOAPvett />} />
                    <Route path="soapvett/history" element={<SOAPHistory />} />
                    <Route path="soapvett/case-log" element={<CaseLogPage />} />
                    <Route
                      path="soapvett/consultation/:id"
                      element={<SOAPConsultationDetail />}
                    />
                    <Route
                      path="soapvett/phonecall/:id"
                      element={<PhoneCallDetail />}
                    />
                    <Route
                      path="soapvett/edit/:id"
                      element={<SOAPEditorPage />}
                    />
                    <Route path="clinicalflow" element={<ClinicFlow />} />
                    <Route path="clinicalflow/:id" element={<ClinicFlow />} />
                    <Route
                      path="clinicalflow/history"
                      element={<ClinicFlow />}
                    />
                    <Route
                      path="chat-statistics"
                      element={<ChatStatistics />}
                    />
                    <Route
                      path="document-management"
                      element={<DocumentManagement />}
                    />
                    <Route path="claims" element={<ClaimsManagement />} />
                  </Route>
                </Route>

                {/* Catch-all: redirigir a idioma detectado */}
                <Route path="*" element={<LanguageRedirect />} />
              </Routes>
            </Suspense>
          </div>
        </AnimatePresence>

        {/* FloatingAIChat SOLO en páginas privadas, NO en páginas públicas ni ai-chat */}
        {(() => {
          const shouldShow =
            user &&
            showAppContent &&
            !location.pathname.includes("/ai-chat") &&
            !location.pathname.includes("/dashboard/chat") &&
            !location.pathname.includes("/dashboard/nala") &&
            (location.pathname.startsWith("/dashboard") ||
              location.pathname.startsWith("/specialist") ||
              location.pathname.startsWith("/admin"));

          return shouldShow ? (
            <Suspense fallback={null}>
              <FloatingAIChat
                position="bottom-right"
                theme={{ primary: "light" }}
              />
            </Suspense>
          ) : null;
        })()}
      </div>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: "#4ade80",
              secondary: "#fff",
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
        }}
      />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppointmentProvider>
        <AppContent />
      </AppointmentProvider>
    </AuthProvider>
  );
};

export default App;
